<template>
  <Preloader v-if="loading" />
  <VRow v-else-if="error">
    <VCol cols="12">
      <VAlert type="error">
        {{ error }}
      </VAlert>
    </VCol>
  </VRow>
  <div v-else>
    <a @click="goToWizard" class="link-dotted">
      {{ $t('returnToMaster') }}
    </a>
    <VacancyQuestionsForm
      :vacancy="vacancy"
      :questions="questions"
      :categories="categories"
      :selected="selected"
      @submit="onSubmit"
      :interviewer-id="interviewerId"
    >
      <template v-slot:buttons="{ disabled, submit }">
        <VBtn
          color="success"
          type="submit"
          :dark="!disabled"
          :disabled="disabled"
          :loading="updateLoading"
          elevation="0"
          @click="onNext(submit)"
        >
          {{ $t('save') }}
        </VBtn>
        <VBtn
          color="success"
          outlined
          dark
          class="ml-3"
          @click="cancel"
        >
          {{ $t('cancel') }}
        </VBtn>
      </template>
    </VacancyQuestionsForm>
  </div>
</template>

<script>
import VacancyScript from '@/orm/models/Interview/VacancyScript';
import { prepareQuestions } from '@/helpers/questions';
import Wizard from '@/orm/models/Wizard/Wizard';
import InterviewVideoQuestionVideo from '@/orm/models/Interview/InterviewVideoQuestionVideo';
import InterviewVideoCategoryVideo from '@/orm/models/Interview/InterviewVideoCategoryVideo';
import { safeGet } from '../../functions';
import VacancyQuestionsForm from './VacancyQuestionsForm.vue';
import Preloader from '../Preloader.vue';
import ValidationMixin from '../../mixins/ValidationMixin';
import Vacancy from '../../orm/models/Vacancy/Vacancy';

export default {
  name: 'VacancyQuestionsEdit',
  metaInfo() {
    return {
      title: this.$t('interviewQuestionList'),
    };
  },
  components: { Preloader, VacancyQuestionsForm },
  mixins: [ValidationMixin],
  data() {
    return {
      loading: true,
      error: null,
      updateLoading: false,
      selected: [],
    };
  },
  computed: {
    vacancy() {
      return Vacancy.query().withAllRecursive().find(this.$route.params.id);
    },
    vacancyQuestions() {
      if (Wizard.modelState().selectedQuestions.length > 0) {
        return Wizard.modelState().selectedQuestions;
      }
      return safeGet(this.vacancy, ['script', 'questions'], []);
    },
    interviewerId() {
      return safeGet(this.vacancy, ['script', 'interviewer', 'id']);
    },
    questions() {
      return InterviewVideoQuestionVideo.query().withAllRecursive().all();
    },
    categories() {
      return InterviewVideoCategoryVideo.query().where('depth', 0).withAllRecursive().all();
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      Wizard.setVacancyId(this.$route.params.id);
      await Wizard.fetchVacancy();
      Wizard.setVacancyId(this.vacancy.id);
      Wizard.setInterviewerId(this.interviewerId);
      await this.withApiErrorHandling(async () => {
        await Promise.all([
          Wizard.fetchData(true),
        ]);
      }, (e) => {
        this.error = e.message;
      });
      this.selected = this.vacancyQuestions;
      if (Wizard.modelState().selectedQuestions.length === 0) {
        Wizard.setSelectedQuestions(this.selected);
      }
    },
    onNext(submit) {
      this.next(submit);
    },
    async next(submit) {
      await submit();
      await VacancyScript.api().store(this.$route.params.id, {
        interviewer: this.interviewerId,
        questions: prepareQuestions(this.selected),
      });
      await Wizard.clearWizardState();
      Wizard.setSelectedQuestions([]);
      await this.withLoading(async () => {
        await Vacancy.api().fetchById(this.$route.params.id);
        await this.$router.push({
          name: 'vacancy',
          params: { vacancyId: this.vacancy.id },
        });
      }, 'updateLoading');
    },
    async onSubmit(questions) {
      this.selected = questions;
      await VacancyScript.api().store(this.$route.params.id, {
        interviewer: this.interviewerId,
        questions: prepareQuestions(this.selected),
      });
    },
    cancel() {
      this.$router.push({
        name: 'vacancy',
        params: { vacancyId: this.vacancy.id },
      });
    },
    goToWizard() {
      Wizard.setIsCompleted(true);
      Wizard.setVacancyId(this.$route.params.id);
      Wizard.saveWizardState().then(() => {
        this.$router.push({
          name: 'vacancy-wizard',
          params: {
            vacancyId: this.$route.params.id,
            interviewerId: this.interviewerId,
            index: 0,
          },
        });
      });
    },
  },
};
</script>

<i18n locale="en" lang="yaml">
  save: Save
  cancel: Cancel
  interviewQuestionList: Interview questions list
  returnToMaster: Back to the Job question selection
</i18n>

<i18n locale="ru" lang="yaml">
  save: Сохранить
  cancel: Отменить
  interviewQuestionList: Список вопросов для интервью
  returnToMaster: Вернуться в мастер подбора вопросов
</i18n>
